import {
  type AudioNoiseSuppressionMode,
  isolationValueForMode,
  loadAudioNoiseSuppressionMode,
} from "./prefs";
import { isNoiseSuppressionSupported } from "./detectNoiseSuppressionCapabilities";
import { DeepFilterNet3Core } from "deepfilternet3-noise-filter";
import { DEFINES } from "../../defines";

export type NoiseSuppressionRuntime = {
  processedStream: MediaStream;
  dispose: () => Promise<void>;
};

type ActiveNoiseSuppressionRuntime = NoiseSuppressionRuntime & {
  refreshSuppressionLevel: () => void;
};

type RuntimeFailureReason = "processor-error";

type CreateNoiseSuppressedRuntimeOptions = {
  onRuntimeFailure?: (reason: RuntimeFailureReason) => void;
};

const runtimeByStream = new WeakMap<MediaStream, ActiveNoiseSuppressionRuntime>();
const activeRuntimes = new Set<ActiveNoiseSuppressionRuntime>();
// The "-r1" suffix is a cache-busting revision, NOT the model architecture
// version (that's the "v3" segment below, which is hardcoded inside the
// `deepfilternet3-noise-filter` package itself and out of our control -- see
// AssetLoader.getAssetUrls() in that package). Bump this suffix (and rename
// the matching directory under wasm/, and update the URLs in
// scripts/import-noise-assets.js) any time the *bytes* of df_bg.wasm or the
// model tarball change for any reason -- including swapping which upstream
// checkpoint we ship, as happened on 2026-08-24/25. This lets deployments
// safely set a long-lived immutable Cache-Control header on this path: the
// URL only ever changes when the content does, so there's no staleness risk
// and no need for a manual CDN purge. See README.md, "Which model checkpoint
// we ship" and "Deployment: caching these assets".
const LOCAL_DEEPFILTER_ASSET_BASE = `${DEFINES.publicPath}wasm/deepfilternet3-r1`;

export const refreshActiveNoiseSuppressionLevels = () => {
  // Update all currently running processors in place so profile switches from
  // the UI do not require stopping/restarting microphone capture.
  let refreshedCount = 0;
  for (const runtime of activeRuntimes) {
    try {
      runtime.refreshSuppressionLevel();
      refreshedCount += 1;
    } catch {
      // best-effort
    }
  }

  return refreshedCount;
};

export const createNoiseSuppressedRuntime = async (
  rawStream: MediaStream,
  mode: AudioNoiseSuppressionMode,
  options: CreateNoiseSuppressedRuntimeOptions = {},
): Promise<NoiseSuppressionRuntime | null> => {
  if (mode === "none") {
    return null;
  }

  if (!isNoiseSuppressionSupported()) {
    throw new Error("Noise suppression not supported in this browser");
  }

  const sourceTrack = rawStream.getAudioTracks()[0];
  if (!sourceTrack) {
    return null;
  }

  // latencyHint 0 (rather than "interactive") asks for the smallest render
  // quantum the platform will give us: measured baseLatency 2.67ms vs 5.33ms
  // for "interactive" on Chrome/macOS. Every millisecond here comes straight
  // off the recording's audio-vs-video offset, since the audio path is
  // delayed by this pipeline while the video path is not.
  const context = new AudioContext({
    sampleRate: 48_000,
    latencyHint: 0,
  });

  if (context.sampleRate !== 48_000) {
    await context.close();
    throw new Error(`Unsupported sample rate: ${context.sampleRate}`);
  }

  const processor = new DeepFilterNet3Core({
    sampleRate: 48_000,
    noiseReductionLevel: isolationValueForMode(mode),
    assetConfig: {
      // Load model and WASM from app-hosted assets to avoid CDN/CORS issues
      // and support air-gapped deployments.
      cdnUrl: LOCAL_DEEPFILTER_ASSET_BASE,
    },
  });

  try {
    await processor.initialize();
  } catch (error) {
    await context.close();
    throw new Error(`Failed to initialize DeepFilterNet3: ${String(error)}`);
  }

  const sourceNode = context.createMediaStreamSource(rawStream);
  const workletNode = await processor.createAudioWorkletNode(context);
  const outputGainNode = context.createGain();
  const destinationNode = context.createMediaStreamDestination();

  sourceNode.connect(workletNode);
  workletNode.connect(outputGainNode);
  outputGainNode.connect(destinationNode);

  const refreshSuppressionLevel = () => {
    // Always read the latest mode/levels from persisted prefs because the
    // active runtime can outlive UI components that initiated the change.
    const activeMode = loadAudioNoiseSuppressionMode();
    processor.setNoiseSuppressionEnabled(activeMode !== "none");
    processor.setSuppressionLevel(isolationValueForMode(activeMode));
  };

  refreshSuppressionLevel();

  let hasBypassedAfterFailure = false;
  const bypassAfterFailure = () => {
    if (hasBypassedAfterFailure) {
      return;
    }
    hasBypassedAfterFailure = true;

    try {
      // Keep recording alive by routing raw mic audio when the worklet fails.
      sourceNode.disconnect();
      workletNode.disconnect();
      sourceNode.connect(outputGainNode);
    } catch {
      // ignore
    }

    try {
      processor.setNoiseSuppressionEnabled(false);
    } catch {
      // ignore
    }

    options.onRuntimeFailure?.("processor-error");
  };

  workletNode.addEventListener("processorerror", bypassAfterFailure);

  const processedTrack = destinationNode.stream.getAudioTracks()[0];
  if (!processedTrack) {
    sourceNode.disconnect();
    workletNode.disconnect();
    outputGainNode.disconnect();
    processor.destroy();
    await context.close();
    throw new Error("Audio worklet produced no output track");
  }
  processedTrack.enabled = sourceTrack.enabled;

  const processedStream = new MediaStream([processedTrack]);

  const runtime: ActiveNoiseSuppressionRuntime = {
    processedStream,
    refreshSuppressionLevel,
    dispose: async () => {
      try {
        workletNode.removeEventListener("processorerror", bypassAfterFailure);
      } catch {
        // ignore
      }

      try {
        sourceNode.disconnect();
        workletNode.disconnect();
        outputGainNode.disconnect();
      } catch {
        // ignore
      }

      try {
        processor.destroy();
      } catch {
        // ignore
      }

      rawStream.getTracks().forEach(track => track.stop());
      processedStream.getTracks().forEach(track => track.stop());

      try {
        await context.close();
      } catch {
        // ignore
      }

      activeRuntimes.delete(runtime);
      runtimeByStream.delete(processedStream);
    },
  };

  runtimeByStream.set(processedStream, runtime);
  activeRuntimes.add(runtime);
  return runtime;
};

export const disposeNoiseSuppressionRuntime = async (stream: MediaStream | null) => {
  if (!stream) {
    return;
  }

  const runtime = runtimeByStream.get(stream);
  if (!runtime) {
    stream.getTracks().forEach(track => track.stop());
    return;
  }

  await runtime.dispose();
};
